import * as Separator from '@radix-ui/react-separator'
import { PlusCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/avatar'
import { Button } from '@/components/button'
import { Skeleton } from '@/components/skeleton'
import type { Publication, Publications } from '@/models/publications'
import api from '@/services/api'

import { BigCard } from './big-card'
import { LabelPrimary } from './label-primary'
import { OthersList } from './others-list'

export function MorePublications() {
  const { publicationType } = useParams()

  const [publications, setPublications] = useState<Publications | null>(null)
  const [mostViewed, setMostViewed] = useState<Publication[] | null>(null)
  const [highlighted, sethighlighted] = useState<Publication | null>(null)
  const [publicationLimit, setPublicationLimit] = useState(10)

  const [blockShowMore, setBlockShowMore] = useState<boolean>()

  useEffect(() => {
    api
      .get<Publications>(
        `/publication?limite=${publicationLimit}&tipo=${publicationType === 'letters' ? 'L' : 'N'}`,
      )
      .then((resp) => {
        setPublications(resp.data)
        if (resp.data.total < 10) {
          setBlockShowMore(true)
        }
      })
      .catch((err) => {
        toast.error(err.response.data.message)
      })
  }, [publicationType, publicationLimit])

  useEffect(() => {
    api
      .get<Publication[]>(
        `/publication/mostviewed?limite=2&tipo=${publicationType === 'letters' ? 'L' : 'N'}`,
      )
      .then((resp) => {
        setMostViewed(resp.data)
      })
      .catch((err) => {
        toast.error(err.response.data.message)
      })
  }, [publicationType])

  useEffect(() => {
    api
      .get<Publication>(
        `/publication/highlighted?tipo=${publicationType === 'letters' ? 'L' : 'N'}`,
      )
      .then((resp) => {
        sethighlighted(resp.data)
      })
      .catch((err) => {
        toast.error(err.response.data.message)
      })
  }, [publicationType])

  function handleShowMore() {
    if (!publications) return

    if (publicationLimit < publications.total) {
      if (publicationLimit + 6 >= publications.total) {
        setPublicationLimit(publications.total)
        setBlockShowMore(true)
        return
      }

      setPublicationLimit(publicationLimit + 6)
    }
  }

  return (
    <div className="content-container flex-1">
      <div className="space-y-20">
        <div className="flex flex-col gap-5">
          <LabelPrimary
            variant={publicationType === 'news' ? 'news' : 'letters'}
            title={
              publicationType === 'news'
                ? 'Últimas notícias'
                : 'Últimas letters'
            }
          />
          <div className="grid gap-5 lg:h-full lg:grid-cols-2 lg:gap-24">
            <div className="flex flex-col justify-between gap-10 2xl:gap-20">
              {publications &&
                publications.publications.slice(0, 3).map((item) => (
                  <div
                    key={item.id_publicacao}
                    className="rounded-md transition hover:bg-zinc-200"
                  >
                    <Link to={`/publication/${item.id_publicacao}`}>
                      <div className="flex h-40 gap-6">
                        {item.imagem !== '' && (
                          <Avatar className="aspect-square h-40 w-40 rounded-md">
                            <AvatarFallback>
                              <Skeleton className="h-full w-full rounded-md" />
                            </AvatarFallback>
                            <AvatarImage
                              src={item.imagem}
                              alt=""
                              className="h-full w-full rounded-md object-cover"
                            />
                          </Avatar>
                        )}
                        <div className="flex flex-1 flex-col gap-1 py-2 lg:gap-6">
                          <h1
                            className={twMerge(
                              `line-clamp-2 text-lg font-bold lg:text-2xl`,
                              publicationType === 'news'
                                ? 'text-publication-news'
                                : 'text-bios-500',
                            )}
                          >
                            {item.titulo}
                          </h1>
                          <p className="text-monochromatic-500 line-clamp-3 xl:line-clamp-2 2xl:line-clamp-3">
                            {item.subtitulo}
                          </p>
                        </div>
                      </div>
                    </Link>
                    <Separator.Root className="h-0.5 bg-zinc-300 last:hidden" />
                  </div>
                ))}
            </div>
            {publications && publications.publications[3] && (
              <BigCard
                publicationType={
                  publicationType === 'news' ? 'news' : 'letters'
                }
                publication={publications.publications[3]}
              />
            )}
          </div>
        </div>

        <div className="space-y-10">
          {mostViewed && (
            <OthersList
              column
              data={mostViewed}
              variant={publicationType === 'news' ? 'news' : 'letters'}
              title={
                publicationType === 'news'
                  ? 'Notícias mais lidas'
                  : 'Letters mais lidas'
              }
            />
          )}
        </div>

        {highlighted && (
          <Link to={`/publication/${highlighted.id_publicacao}`}>
            <div
              className={twMerge(
                `mb-14 grid rounded-2xl xl:min-h-125 xl:grid-cols-2 2xl:min-h-150`,
                publicationType === 'news'
                  ? 'bg-publication-news'
                  : 'bg-bios-green-3000',
              )}
            >
              <div className="relative h-40 w-full overflow-hidden select-none lg:h-full">
                {highlighted && (
                  <img
                    src={highlighted.imagem}
                    alt="publicações destaque"
                    className="absolute h-full w-full rounded-t-2xl object-cover lg:rounded-r-none lg:rounded-bl-2xl"
                  />
                )}
              </div>
              <div className="flex flex-col gap-5 px-8 py-10 xl:px-12 2xl:px-24 2xl:py-20">
                {highlighted && (
                  <div className="flex flex-row items-center justify-center lg:justify-start">
                    <h1 className="card-title text-shadow-title text-center text-2xl text-white lg:text-start 2xl:text-4xl">
                      {highlighted.titulo}
                    </h1>
                  </div>
                )}

                {highlighted && (
                  <div className="flex h-full justify-center lg:justify-start">
                    <p className="line-clamp-9 text-center text-white lg:text-start">
                      {highlighted.subtitulo}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Link>
        )}
      </div>

      {!highlighted && <Skeleton className="min-h-96 rounded-lg" />}

      {publications && publications.total >= 5 && (
        <OthersList
          column
          data={publications.publications.slice(4)}
          variant={publicationType === 'news' ? 'news' : 'letters'}
          title={
            publicationType === 'news' ? 'Outras notícias' : 'Outras letters'
          }
        />
      )}

      <Button
        onClick={handleShowMore}
        variant="ghost"
        disabled={blockShowMore}
        className="mx-auto flex items-center gap-2 text-lg disabled:border-zinc-200 disabled:bg-zinc-200 disabled:text-zinc-50"
      >
        <PlusCircle />
        Mostrar mais
      </Button>
    </div>
  )
}
